import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { CategoryParam, DataCard, formatDate, GroupParam, StatusShipment } from '../components/utils';
import { ShipmentsService } from '@app/modules/shipments/shipments.service';
import { BehaviorSubject } from 'rxjs';
import { NAME_ROUTES } from '../constants/routes';

@Injectable({
  providedIn: 'root'
})
export class ShipmentStoreService {
  private shipmentService = inject(ShipmentsService);

  private readonly _allShipments = signal(new Map<string, DataCard[]>());
  private readonly _computedCache = new Map<string, Signal<DataCard[] | undefined>>();

  private pagedShipmentsSubject = new BehaviorSubject<DataCard[]>([]);
  pagedShipments$ = this.pagedShipmentsSubject.asObservable();

  private getKey(category: string, group: string): string {
    return `${category}-${group}`;
  }

  load(category: string, group: string): void {
    const key = this.getKey(category, group);
    if (this._allShipments().has(key)) return;

    this.shipmentService.getListShipments(category, group).subscribe((list) => {
      const formattedList = this.processList(category, group, list);
      const newCache = new Map(this._allShipments());
      newCache.set(key, formattedList);
      this._allShipments.set(newCache);
    });
  }

  refresh(category: string, group: string): void {
    const key = this.getKey(category, group);
    this.shipmentService.getListShipments(category, group).subscribe((list) => {
      const formattedList = this.processList(category, group, list);
      const newCache = new Map(this._allShipments());
      newCache.set(key, formattedList);
      this._allShipments.set(newCache);
    });
  }

  refreshAll(): void {
    const keys = Array.from(this._allShipments().keys());

    for (const key of keys) {
      const sepIndex = key.indexOf('-');
      if (sepIndex === -1) {
        continue;
      }
      const category = key.slice(0, sepIndex) as CategoryParam;
      const group = key.slice(sepIndex + 1) as GroupParam;
      this.refresh(category, group);
    }
  }

  private processList(category: string, group: string, list: DataCard[]): DataCard[] {
    let taggedList = list.map(item => ({
      ...item,
      category: category === CategoryParam.pending ? NAME_ROUTES.HOME : (category === CategoryParam.shipment ? NAME_ROUTES.SHIPMENTS : NAME_ROUTES.RETURNS),
      group
    }));

    if ((category === CategoryParam.shipment && group === GroupParam.pending) || (category === CategoryParam.return && group === GroupParam.pending)) {
      const toPayList = taggedList
        .filter(item => item.status === StatusShipment.toPay)
        .sort((a, b) => {
          const dateA = new Date(a.dateCreated).getTime();
          const dateB = new Date(b.dateCreated).getTime();
          return dateA - dateB;
        });

      const others = taggedList.filter(item => item.status !== StatusShipment.toPay);
      taggedList = [...toPayList, ...others];
    }

    if ((category === CategoryParam.shipment && group === GroupParam.in_transit) || (category === CategoryParam.return && group === GroupParam.in_transit)) {
      taggedList.sort((a, b) => {
        const isADispatched = a.status === StatusShipment.dispatched;
        const isBDispatched = b.status === StatusShipment.dispatched;

        if (isADispatched && !isBDispatched) return -1;
        if (!isADispatched && isBDispatched) return 1;

        if (isADispatched && isBDispatched) {
          const dateA = new Date(a.dateCreated).getTime();
          const dateB = new Date(b.dateCreated).getTime();
          return dateA - dateB;
        }
        return 0;
      });
    }

    if ((category === CategoryParam.shipment && group === GroupParam.completed) || (category === CategoryParam.return && group === GroupParam.completed)) {
      taggedList.sort((a, b) => {
        const isADelivered = a.status === StatusShipment.delivered;
        const isBDelivered = b.status === StatusShipment.delivered;

        if (isADelivered && !isBDelivered) return -1;
        if (!isADelivered && isBDelivered) return 1;

        if (isADelivered && isBDelivered) {
          const dateA = a.dateStatus ? new Date(a.dateStatus).getTime() : 0;
          const dateB = b.dateStatus ? new Date(b.dateStatus).getTime() : 0;
          return dateB - dateA;
        }
        return 0;
      });
    }

    const formattedList = taggedList.map(item => ({
      ...item,
      dateCreated: formatDate(item.dateCreated),
      dateStatus: item.dateStatus ? formatDate(item.dateStatus) : '',
      deliveryDate: item.deliveryDate ? formatDate(item.deliveryDate) : ''
    }));

    return formattedList;
  }

  getSignal(category: string, group: string): Signal<DataCard[] | undefined> {
    const key = this.getKey(category, group);

    if (!this._computedCache.has(key)) {
      this._computedCache.set(
        key,
        computed(() => this._allShipments().get(key))
      );
    }

    return this._computedCache.get(key)!;
  }

  updatePagedShipments(shipments: DataCard[]) {
    this.pagedShipmentsSubject.next(shipments);
  }

  getAllShipmentsSignal(): Signal<DataCard[]> {
    return computed(() => Array.from(this._allShipments().values()).flat());
  }

  getHasAnyNonEmptyListSignal(): Signal<boolean> {
    return computed(() => {
      const all = this._allShipments();
      for (const list of all.values()) {
        if (list.length > 0) return true;
      }
      return false;
    });
  }

  clear(): void {
    this._allShipments.set(new Map());
    this._computedCache.clear();
    this.pagedShipmentsSubject.next([]);
  }
}
